/* Lines feature (W2-07): panel, tree rows, filter chips, dialogs. Design tokens only. */

.panel {
  container-type: inline-size;
  container-name: lines;
  /* UX-027 (CR-W3-03-13): the Lines tree (4 Hz NOW poll + panel re-renders) must stay inside its own layer;
     `.nowCell` is already `contain: paint` (UX-053). Hiding the panel lifts the shipped scene ~5-13 fps. */
  contain: paint;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  background: var(--color-surface-1);
  color: var(--color-text);
  font-size: var(--text-sm);
}

/* ------------------------------ header ------------------------------ */

.header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1) var(--space-2);
  padding: var(--space-1) var(--space-2);
  border-bottom: var(--border-width) solid var(--color-divider);
}

.title {
  font-weight: var(--weight-semibold);
  font-size: var(--text-xs);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  white-space: nowrap;
}

.headerGroup {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.spacer {
  flex: 1 1 auto;
}

.search {
  flex: 1 1 120px;
  min-width: 90px;
  max-width: 220px;
  height: var(--control-h-sm);
  padding: 0 var(--space-2);
  border: var(--border-width) solid var(--color-border-input);
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
  color: var(--color-text);
  font: inherit;
}

.search:focus-visible,
.input:focus-visible,
.renameInput:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* ------------------------------ filter chips ------------------------------ */

.chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-2);
  border-bottom: var(--border-width) solid var(--color-divider);
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  height: calc(22px * var(--ui-scale));
  padding: 0 var(--space-2);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-pill);
  background: var(--color-surface-2);
  color: var(--color-text-muted);
  font: inherit;
  font-size: var(--text-xs);
  cursor: pointer;
  white-space: nowrap;
}

.chip:hover {
  background: var(--color-surface-hover);
  color: var(--color-text);
}

.chip[aria-pressed='true'],
.chip[data-on='true'] {
  border-color: var(--color-accent-border);
  background: var(--color-accent-muted);
  color: var(--color-text);
}

.chip:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.chipRemove {
  display: inline-flex;
  border: 0;
  padding: 0;
  background: none;
  color: inherit;
  cursor: pointer;
}

.chipLabel {
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
}

.checkList {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 180px;
  max-height: 320px;
  overflow: auto;
  padding: var(--space-1);
}

.checkRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

/* ------------------------------ status strips ------------------------------ */

.status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  border-bottom: var(--border-width) solid var(--color-divider);
  font-size: var(--text-xs);
}

.statusDiffers {
  background: var(--color-warning-muted);
  color: var(--color-text);
}

.statusIcon {
  display: inline-flex;
  color: var(--color-warning);
}

.statusMuted {
  color: var(--color-text-muted);
}

.linkButton {
  border: 0;
  padding: 0;
  background: none;
  color: var(--color-text-link);
  font: inherit;
  cursor: pointer;
  text-decoration: underline;
}

.linkButton:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* ------------------------------ tree ------------------------------ */

.treeWrap {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.tree {
  --row-h: calc(26px * var(--ui-scale));
  /*
   * Fixed (scaled) px widths, never em: the header row's smaller font would otherwise resolve the same columns
   * narrower than the rows' and "NOTES" drew over "NOW" (UX-054). Columns: toggles, #, name, type, notes, now, Z,
   * group.
   */
  --cols: calc(66px * var(--ui-scale)) calc(34px * var(--ui-scale)) minmax(90px, 1fr) calc(84px * var(--ui-scale))
    calc(46px * var(--ui-scale)) calc(30px * var(--ui-scale)) calc(34px * var(--ui-scale)) calc(96px * var(--ui-scale));
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  outline: none;
}

.tree:focus-visible {
  box-shadow: inset 0 0 0 var(--focus-ring-width) var(--color-focus-ring);
}

.headRow,
.row {
  display: grid;
  grid-template-columns: var(--cols);
  align-items: center;
  column-gap: var(--space-1);
  padding: 0 var(--space-2) 0 var(--space-1);
}

.headRow > [role='columnheader'] {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.headRow {
  position: sticky;
  top: 0;
  z-index: 1;
  height: calc(22px * var(--ui-scale));
  background: var(--color-surface-2);
  border-bottom: var(--border-width) solid var(--color-divider);
  color: var(--color-text-subtle);
  font-size: var(--text-2xs);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.body {
  position: relative;
}

.row {
  position: absolute;
  left: 0;
  right: 0;
  height: var(--row-h);
  cursor: default;
  user-select: none;
  border-top: var(--border-width) solid transparent;
  border-bottom: var(--border-width) solid transparent;
}

.row:hover {
  background: var(--color-surface-hover);
}

.row[aria-selected='true'] {
  background: var(--color-selection);
}

.row[data-active='true'] .name {
  font-weight: var(--weight-semibold);
  color: var(--color-text);
}

.row[data-active='true']::before {
  content: '';
  position: absolute;
  left: 0;
  top: 2px;
  bottom: 2px;
  width: 3px;
  border-radius: var(--radius-xs);
  background: var(--color-accent);
}

.row[data-cursor='true'] {
  box-shadow: inset 0 0 0 1px var(--color-accent-border);
}

.row[data-context='true'] {
  opacity: 0.6;
}

.row[data-muted='true'] .name,
.row[data-muted='true'] .typeLabel {
  color: var(--color-text-subtle);
}

.row[data-drop='before'] {
  border-top-color: var(--color-accent);
}

.row[data-drop='after'] {
  border-bottom-color: var(--color-accent);
}

.row[data-drop='onto'] {
  box-shadow: inset 0 0 0 2px var(--color-accent);
}

.row[data-drop-refused='true'] {
  box-shadow: inset 0 0 0 2px var(--color-danger);
  border-color: transparent;
}

.toggles {
  display: flex;
  align-items: center;
}

.toggleOff {
  opacity: 0.35;
}

.toggleOff:hover,
.row:hover .toggleOff,
.row[aria-selected='true'] .toggleOff {
  opacity: 0.75;
}

.index {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  color: var(--color-text-muted);
  text-align: right;
}

.nameCell {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  min-width: 0;
}

.twisty {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex: none;
  border: 0;
  padding: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.twisty:hover {
  background: var(--color-surface-active);
  color: var(--color-text);
}

.twistySpace {
  width: 16px;
  flex: none;
}

.typeIcon {
  display: inline-flex;
  flex: none;
  color: var(--color-text-muted);
}

.tagDot {
  width: 8px;
  height: 8px;
  flex: none;
  border-radius: var(--radius-pill);
  border: var(--border-width) solid var(--color-border-strong);
}

.name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--color-text);
}

.unnamed {
  font-style: italic;
  color: var(--color-text-subtle);
}

.badges {
  display: inline-flex;
  align-items: center;
  gap: var(--space-0);
  flex: none;
  color: var(--color-text-muted);
}

.badgeButton {
  display: inline-flex;
  border: 0;
  padding: 0;
  background: none;
  color: inherit;
  cursor: pointer;
}

.renameInput {
  flex: 1 1 auto;
  min-width: 0;
  height: calc(22px * var(--ui-scale));
  padding: 0 var(--space-1);
  border: var(--border-width) solid var(--color-accent-border);
  border-radius: var(--radius-xs);
  background: var(--color-surface-sunken);
  color: var(--color-text);
  font: inherit;
}

.typeLabel {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--color-text-muted);
}

.num {
  font-variant-numeric: tabular-nums;
  text-align: right;
  color: var(--color-text-muted);
}

/* The "Now" column: its own centred cell, clear of the notes count (UX-054). */
.colNow,
.nowCell {
  display: flex;
  justify-content: center;
  text-align: center;
}

.nowCell {
  contain: paint;
}

.now {
  display: inline-flex;
  justify-content: center;
  color: var(--color-text-subtle);
}

/* The group column's header: its name, or an icon when the column only shows swatches. */
.colGroupIcon {
  display: none;
}

.nowAnimating {
  color: var(--color-accent);
}

.groupCell {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  min-width: 0;
  color: var(--color-text-muted);
}

.groupName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropNote {
  position: absolute;
  right: var(--space-2);
  z-index: 2;
  max-width: 70%;
  padding: var(--space-0) var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-danger);
  color: var(--color-text-on-danger);
  font-size: var(--text-xs);
  pointer-events: none;
  box-shadow: var(--shadow-sm);
}

.rootDrop {
  flex: none;
  min-height: calc(26px * var(--ui-scale));
  display: flex;
  align-items: center;
  justify-content: center;
  margin: var(--space-1) var(--space-2);
  border: var(--border-width) dashed var(--color-border-strong);
  border-radius: var(--radius-sm);
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
}

.rootDrop[data-drop='root'] {
  border-color: var(--color-accent);
  color: var(--color-text);
  background: var(--color-accent-muted);
}

.empty {
  padding: var(--space-4);
}

/* Narrow dock: drop the type label and the group name, keep the icons and swatches. */
@container lines (max-width: 520px) {
  /* Header and chips stay on one row each (UX-051): icon-only view switch and toggle chips, names kept for
     screen readers. */
  .segText,
  .chipText {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
  .header {
    column-gap: var(--space-1);
  }
  .search {
    flex-basis: 56px;
    min-width: 56px;
  }

  .tree {
    --cols: calc(66px * var(--ui-scale)) calc(30px * var(--ui-scale)) minmax(80px, 1fr) 0 calc(46px * var(--ui-scale))
      calc(28px * var(--ui-scale)) calc(30px * var(--ui-scale)) calc(20px * var(--ui-scale));
  }
  .groupName,
  .colGroupText {
    display: none;
  }
  /* Grid cells keep their slot (a removed cell would shift every later cell one column left, UX-054). */
  .typeLabel,
  .colType {
    visibility: hidden;
    overflow: hidden;
  }
  .colGroupIcon {
    display: inline-flex;
  }
}

@container lines (max-width: 380px) {
  .tree {
    --cols: calc(66px * var(--ui-scale)) calc(28px * var(--ui-scale)) minmax(70px, 1fr) 0 calc(42px * var(--ui-scale))
      calc(26px * var(--ui-scale)) 0 calc(20px * var(--ui-scale));
  }
  .colZ,
  .zCell {
    visibility: hidden;
    overflow: hidden;
  }
}

/* ------------------------------ dialogs ------------------------------ */

.form {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.fieldRow {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.label {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.input {
  height: var(--control-h-md);
  padding: 0 var(--space-2);
  border: var(--border-width) solid var(--color-border-input);
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
  color: var(--color-text);
  font: inherit;
}

.help {
  font-size: var(--text-xs);
  color: var(--color-text-subtle);
}

.error {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--color-danger-muted);
  color: var(--color-text);
}

.warning {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--color-warning-muted);
  color: var(--color-text);
}

.info {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--color-info-muted);
  color: var(--color-text);
}

.choices {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  border: 0;
  margin: 0;
  padding: 0;
}

.choice {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--space-1) var(--space-2);
  align-items: start;
  padding: var(--space-2) var(--space-3);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-2);
  cursor: pointer;
}

.choice:has(input:checked) {
  border-color: var(--color-accent-border);
  background: var(--color-accent-muted);
}

.choice:has(input:focus-visible) {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.choice input {
  margin: 3px 0 0;
  accent-color: var(--color-accent);
}

.choiceTitle {
  font-weight: var(--weight-medium);
}

.choiceText {
  grid-column: 2;
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.choice[data-refused='true'] {
  cursor: not-allowed;
  background: var(--color-surface-sunken);
}

.choice[data-refused='true'] .choiceTitle {
  color: var(--color-text-disabled);
}

.choiceRefused {
  grid-column: 2;
  display: flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--text-xs);
  color: var(--color-text);
}

.typeGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: var(--space-2);
}

.list {
  margin: 0;
  padding-left: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.diagram {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
  padding: var(--space-2);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
}

.diagramFrame {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.diagramSvg {
  width: 100%;
  max-width: 160px;
  height: auto;
}

.svgParent {
  stroke: var(--color-accent);
  stroke-width: 3;
  stroke-linecap: round;
}

.svgChild {
  stroke: var(--color-note-drag);
  stroke-width: 3;
  stroke-linecap: round;
}

.svgGuide {
  stroke: var(--color-curve-guide);
  stroke-width: 1;
  stroke-dasharray: 3 3;
  fill: none;
}

.svgDot {
  fill: var(--color-text-muted);
}

.sentence {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

/* ------------------------------ pickers (switcher, images, elements) ------------------------------ */

.pickerInput {
  width: 100%;
  height: var(--control-h-lg);
  padding: 0 var(--space-3);
  border: var(--border-width) solid var(--color-border-input);
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-md);
}

.pickerInput:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.pickerList {
  display: flex;
  flex-direction: column;
  max-height: min(50vh, 360px);
  overflow: auto;
  margin-top: var(--space-2);
  padding: 0;
  list-style: none;
}

.option {
  display: grid;
  grid-template-columns: 3.2em 18px 1fr auto;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.option[aria-selected='true'] {
  background: var(--color-selection);
}

.option[aria-disabled='true'] {
  cursor: not-allowed;
  color: var(--color-text-disabled);
}

.optionDetail {
  font-size: var(--text-xs);
  color: var(--color-text-subtle);
  white-space: nowrap;
}

.pickerStatus {
  margin-top: var(--space-1);
  font-size: var(--text-xs);
  color: var(--color-text-subtle);
}

.tileGrid {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: var(--space-2);
}

.tile {
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-2);
  color: var(--color-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.tile:hover {
  background: var(--color-surface-hover);
}

.tile:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.tileText {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.tileTitle {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tileDetail {
  font-size: var(--text-xs);
  color: var(--color-text-subtle);
}

/* ------------------------------ groups editor ------------------------------ */

.groupList {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin: 0;
  padding: 0;
  list-style: none;
}

.groupRow {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-surface-2);
}

.groupCount {
  font-size: var(--text-xs);
  color: var(--color-text-subtle);
  white-space: nowrap;
}

.groupActions {
  display: flex;
  align-items: center;
}

.newGroup {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--space-2);
}

/* ------------------------------ status bar chip ------------------------------ */

.activeChip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  max-width: 180px;
  height: calc(20px * var(--ui-scale));
  padding: 0 var(--space-2);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-pill);
  background: var(--color-surface-2);
  color: var(--color-text-muted);
  font: inherit;
  font-size: var(--text-xs);
  cursor: pointer;
}

.activeChip:hover {
  color: var(--color-text);
  background: var(--color-surface-hover);
}

.activeChip:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.activeChipText {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.visuallyHidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

@media (prefers-reduced-motion: no-preference) {
  .row,
  .chip,
  .tile {
    transition: background-color var(--duration-fast) ease;
  }
}
